import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';

function CarDetails() {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [car, setCar] = useState(null);
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

    const { startDate, endDate } = location.state || {};

    useEffect(() => {
        const fetchCarDetails = async () => {
            setLoading(true);
            try {
                const response = await axios.get('https://api.karrentals.ca/search-cars', {
                    params: { car_id: id },
                });
                if (response.data.success && response.data.data.length > 0) {
                    setCar(response.data.data[0]);
                } else {
                    setError('Car details not found.');
                }
            } catch (err) {
                console.error('Error fetching car details:', err);
                setError('Unable to fetch car details.');
            } finally {
                setLoading(false);
            }
        };

        const fetchCarPhotos = async () => {
            try {
                const response = await axios.get(`https://api.karrentals.ca/car-photos/${id}`);
                const S3_BASE = 'https://karrental.s3.amazonaws.com/';
                const raw = response.data.photos || [];
                const normalized = raw.map((p) => {
                    if (typeof p === 'string') {
                        return p.startsWith('http') ? p : `${S3_BASE}${p}`;
                    }
                    return p.full_url || `${S3_BASE}${p.photo_url}`;
                });
                setPhotos(normalized);
            } catch (err) {
                console.error('Error fetching car photos:', err);
            }
        };

        fetchCarDetails();
        fetchCarPhotos();
    }, [id]);

    useEffect(() => {
        if (car) {
            document.title = `${car.year} ${car.name} ${car.model} | KAR – Kelowna Auto Rentals`;
            document.querySelector('meta[name="description"]')?.setAttribute("content",
                `Rent the ${car.year} ${car.name} ${car.model} in Kelowna or Vancouver. Contactless pickup at YLW & YVR. Book online with KAR – Kelowna Auto Rentals.`
            );
        } else {
            document.title = "Car Details | KAR – Kelowna Auto Rentals";
        }
    }, [car]);

    const toggleDescription = () => {
        setIsDescriptionExpanded((prev) => !prev);
    };

    const renderImage = (item) => {
        return (
            <div className="relative w-full" style={{ paddingTop: '56.25%' /* 16:9 Aspect Ratio */ }}>
                <img
                    src={item.original}
                    alt={item.originalAlt}
                    className="absolute top-0 left-0 w-full h-full object-cover"
                />
            </div>
        );
    };

    if (loading) return <p>Loading...</p>;
    if (error) return <p className="text-red-500">{error}</p>;

    return (
        <Layout>
            <div className="container mx-auto px-4 py-8">
                <button
                    className="text-blue-500 hover:underline mb-4"
                    onClick={() => navigate(-1)}
                >
                    &lt; Back to Search
                </button>

                {car && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {/* Image Gallery */}
                            <div className="md:col-span-2">
                                <div
                                    className="w-full h-auto overflow-hidden rounded-lg cursor-pointer"
                                >
                                    {photos.length > 0 ? (
                                        <ImageGallery
                                            items={photos.map(photo => ({
                                                original: photo,
                                                thumbnail: photo,
                                                originalClass: 'custom-image-gallery',
                                                thumbnailClass: 'custom-thumbnail',
                                                originalAlt: `Photo of ${car.name}`,
                                                thumbnailAlt: 'Thumbnail'
                                            }))}
                                            showFullscreenButton={true}
                                            showPlayButton={false}
                                            showThumbnails={true}
                                            showBullets={true}
                                            slideInterval={3000}
                                            slideOnThumbnailOver={true}
                                            renderItem={renderImage}
                                        />
                                    ) : (
                                        <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
                                            <img
                                                src="https://via.placeholder.com/800x450?text=No+Image"
                                                alt="Placeholder"
                                                className="absolute top-0 left-0 w-full h-full object-cover"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            {/* Booking Details - Moves next to images on md and lg screens */}
                            <div className="hidden md:block p-4 rounded-lg border-white border-2 md:col-span-1">
                                <h2 className="text-2xl font-bold mb-4">Booking Details</h2>
                                <div className="mb-4">
                                    <p>
                                        <strong>Trip Start:</strong>{' '}
                                        {startDate
                                            ? new Date(startDate).toLocaleString()
                                            : 'Not Selected'}
                                    </p>
                                </div>
                                <div className="mb-4">
                                    <p>
                                        <strong>Trip End:</strong>{' '}
                                        {endDate
                                            ? new Date(endDate).toLocaleString()
                                            : 'Not Selected'}
                                    </p>
                                </div>
                                <p className="text-xl font-bold my-4">
                                    Total: $
                                    {startDate && endDate
                                        ? car.price_per_day * Math.max(1, Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)))
                                        : car.price_per_day}
                                    <p className="text-xs font-bold italic">
                                        excluding taxes and fees
                                    </p>
                                </p>
                                <p className="text-sm font-bold italic my-4">
                                    {car.price_per_day} per day
                                </p>
                                <button
                                    className="w-full px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                    onClick={() =>
                                        navigate('/checkout', {
                                            state: {
                                                carId: car.id,
                                                totalPrice:
                                                    startDate && endDate
                                                        ? car.price_per_day *
                                                        Math.max(1, Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)))
                                                        : car.price_per_day,
                                                startDate,
                                                endDate,
                                            },
                                        })
                                    }
                                >
                                    Continue to Book
                                </button>
                                <div className="mt-6 text-lg text-center text-gray-200">
                                    <ul className="space-y-2">
                                        <li>Must be 25+</li>
                                        <li>Driver's License Required</li>
                                        <li>Insurance Included</li>
                                        <li>200 Kms per day included</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        {/* Booking Details for Small Screens */}
                        <div className="block md:hidden p-4 rounded-lg border-white border-2 my-4">
                            <h2 className="text-2xl font-bold mb-4">Booking Details</h2>
                            <div className="mb-4">
                                <p>
                                    <strong>Trip Start:</strong>{' '}
                                    {startDate
                                        ? new Date(startDate).toLocaleString()
                                        : 'Not Selected'}
                                </p>
                            </div>
                            <div className="mb-4">
                                <p>
                                    <strong>Trip End:</strong>{' '}
                                    {endDate
                                        ? new Date(endDate).toLocaleString()
                                        : 'Not Selected'}
                                </p>
                            </div>
                            <p className="text-xl font-bold my-4">
                                Total: $
                                {startDate && endDate
                                    ? car.price_per_day * Math.max(1, Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)))
                                    : car.price_per_day}
                                <p className="text-xs font-bold italic">
                                    excluding taxes and fees
                                </p>
                            </p>
                            <p className="text-sm font-bold italic my-4">
                                {car.price_per_day} per day
                            </p>
                            <button
                                className="w-full px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                onClick={() =>
                                    navigate('/checkout', {
                                        state: {
                                            carId: car.id,
                                            totalPrice:
                                                startDate && endDate
                                                    ? car.price_per_day *
                                                    Math.max(1, Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)))
                                                    : car.price_per_day,
                                            startDate,
                                            endDate,
                                        },
                                    })
                                }
                            >
                                Continue to Book
                            </button>
                            <div className="mt-6 text-lg text-center text-gray-200">
                                    <ul className="space-y-2">
                                        <li>Must be 25+</li>
                                        <li>Driver's License Required</li>
                                        <li>Insurance Included</li>
                                        <li>200 Kms per day included</li>
                                    </ul>
                                </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
                            <div className="md:col-span-3">
                                <h1 className="text-3xl font-bold mb-2">
                                    {car.year} {car.name} {car.model}
                                </h1>
                                {car && (
                                    <div className="grid grid-cols-4 gap-4 mt-6">
                                        <div className="flex flex-col items-center text-center">
                                            <img
                                                src="/icons/engine-icon.png"
                                                alt="Engine"
                                                className="w-12 h-12 mb-2"
                                            />
                                            <span className="text-lg">{car.engine_type}</span>
                                        </div>
                                        <div className="flex flex-col items-center text-center">
                                            <img
                                                src="/icons/luggage-icon.png"
                                                alt="Luggage"
                                                className="w-12 h-12 mb-2"
                                            />
                                            <span className="text-lg">{car.bags} Bags</span>
                                        </div>
                                        <div className="flex flex-col items-center text-center">
                                            <img
                                                src="/icons/seat-icon.png"
                                                alt="Seats"
                                                className="w-12 h-12 mb-2"
                                            />
                                            <span className="text-lg">{car.passengers} Seats</span>
                                        </div>
                                        <div className="flex flex-col items-center text-center">
                                            <img
                                                src="/icons/door-icon.png"
                                                alt="Doors"
                                                className="w-12 h-12 mb-2"
                                            />
                                            <span className="text-lg">{car.doors} Doors</span>
                                        </div>
                                    </div>
                                )}
                                <h2 className="text-2xl font-bold mt-6 mb-4">Description</h2>
                                <div
                                    className={`text-lg my-2 leading-relaxed text-gray-200 ${
                                        isDescriptionExpanded ? '' : 'line-clamp-4'
                                    }`}
                                    dangerouslySetInnerHTML={{ __html: car.description }}
                                ></div>
                                <button
                                    className="text-blue-500 hover:underline mt-2"
                                    onClick={toggleDescription}
                                >
                                    {isDescriptionExpanded ? 'Read Less' : 'Read More'}
                                </button>
                                {car.features && (
                                    <>
                                        <h2 className="text-2xl font-bold mt-6 mb-4">Features</h2>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            {car.features.split(',').map((feature, index) => (
                                                <div key={index} className="text-lg flex items-start">
                                                    <span className="mr-2 text-blue-500">•</span>
                                                    <span>{feature.trim()}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Layout>
    );
}

export default CarDetails;
